import { APIResponse } from "../models/NetworkResponse";
import {
  getAPI,
  postAPI,
  putAPI,
  deleteAPI,
  postMultipartAPI,
} from "./Networking";
import type {
  PlacementType,
  PSDTemplate,
  SmartObject,
  SmartObjectDefault,
  ProductColor,
  BackgroundLayer,
  GeneratedMockup,
  GeneratedMockupSource,
  GeneratedMockupActionState,
} from "../models/MockupConfig";

export default class MockupConfigAPI {
  getPlacementTypes = async (): Promise<PlacementType[]> => {
    try {
      const response = await getAPI("/v5/mockup-config/placement-types");
      const data = response.result;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      throw error;
    }
  };

  uploadPsd = async (payload: {
    file: File;
    product_id: number;
    reference_id?: string;
    placetype_id: number;
  }): Promise<APIResponse> => {
    const formData = new FormData();
    formData.append("file", payload.file);
    formData.append("product_id", String(payload.product_id));
    if (payload.reference_id?.trim()) {
      formData.append("reference_id", payload.reference_id.trim());
    }
    formData.append("placetype_id", String(payload.placetype_id));
    return postMultipartAPI("/v5/mockup-config/upload-psd", formData);
  };

  getPSDTemplates = async (
    catalogueIdOrProductId: number,
    options?: { useProductId?: boolean },
  ): Promise<PSDTemplate[]> => {
    try {
      const params = options?.useProductId
        ? { product_id: catalogueIdOrProductId }
        : { product_id: catalogueIdOrProductId };
      const response = await getAPI("/v5/mockup-config/psd-templates", params);
      const result = response.result;
      const data = Array.isArray(result)
        ? result
        : (result?.list ?? result?.data ?? []);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      throw error;
    }
  };

  getPSDTemplate = async (id: number): Promise<PSDTemplate> => {
    try {
      const response = await getAPI(`/v5/mockup-config/psd-templates/${id}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  ensureBlankPreviewUrl = async (psdTemplateId: number): Promise<string> => {
    const response = await postAPI(
      `/v5/mockup-config/psd-templates/${psdTemplateId}/blank-preview`,
      {},
    );
    const url = response.result?.blank_preview_url;
    if (!url || typeof url !== "string") {
      throw new Error("Blank preview URL was not returned");
    }
    return url;
  };

  createPSDTemplate = async (
    data: Omit<PSDTemplate, "id">,
  ): Promise<PSDTemplate & { id: number }> => {
    try {
      const response = await postAPI("/v5/mockup-config/psd-templates", data);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updatePSDTemplate = async (
    id: number,
    data: Partial<PSDTemplate>,
  ): Promise<PSDTemplate> => {
    try {
      const response = await putAPI(
        `/v5/mockup-config/psd-templates/${id}`,
        data,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  deletePSDTemplate = async (id: number): Promise<void> => {
    try {
      await deleteAPI(`/v5/mockup-config/psd-templates/${id}`);
    } catch (error) {
      throw error;
    }
  };

  getSmartObjects = async (psdTemplateId: number): Promise<SmartObject[]> => {
    try {
      const response = await getAPI("/v5/mockup-config/smart-objects", {
        psd_template_id: psdTemplateId,
      });
      const data = response.result;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      throw error;
    }
  };

  createSmartObject = async (
    data: Omit<SmartObject, "id">,
  ): Promise<SmartObject & { id: number }> => {
    try {
      const response = await postAPI("/v5/mockup-config/smart-objects", data);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateSmartObject = async (
    id: number,
    data: Partial<SmartObject>,
  ): Promise<SmartObject> => {
    try {
      const response = await putAPI(
        `/v5/mockup-config/smart-objects/${id}`,
        data,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  deleteSmartObject = async (id: number): Promise<void> => {
    try {
      await deleteAPI(`/v5/mockup-config/smart-objects/${id}`);
    } catch (error) {
      throw error;
    }
  };

  getSmartObjectDefaults = async (
    smartObjectId: number,
  ): Promise<SmartObjectDefault | null> => {
    try {
      const response = await getAPI(
        `/v5/mockup-config/smart-object-defaults/${smartObjectId}`,
      );
      return response.result ?? null;
    } catch {
      return null;
    }
  };

  createSmartObjectDefault = async (
    data: Omit<SmartObjectDefault, "id">,
  ): Promise<SmartObjectDefault & { id: number }> => {
    try {
      const response = await postAPI(
        "/v5/mockup-config/smart-object-defaults",
        data,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateSmartObjectDefault = async (
    smartObjectId: number,
    data: Partial<SmartObjectDefault>,
  ): Promise<SmartObjectDefault> => {
    try {
      const response = await postAPI(
        "/v5/mockup-config/smart-object-defaults",
        { smart_object_id: smartObjectId, ...data },
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getColors = async (catalogueId: number): Promise<ProductColor[]> => {
    try {
      const response = await getAPI("/v5/mockup-config/colors", {
        catalogue_id: catalogueId,
      });
      const data = response.result;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      throw error;
    }
  };

  createColor = async (
    data: Omit<ProductColor, "id">,
  ): Promise<ProductColor & { id: number }> => {
    try {
      const response = await postAPI("/v5/mockup-config/colors", data);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  deleteColor = async (id: number): Promise<void> => {
    try {
      await deleteAPI(`/v5/mockup-config/colors/${id}`);
    } catch (error) {
      throw error;
    }
  };

  getBackgroundLayers = async (
    psdTemplateId: number,
  ): Promise<BackgroundLayer[]> => {
    try {
      const response = await getAPI("/v5/mockup-config/background-layers", {
        psd_template_id: psdTemplateId,
      });
      const data = response.result;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      throw error;
    }
  };

  createBackgroundLayer = async (
    data: Omit<BackgroundLayer, "id">,
  ): Promise<BackgroundLayer & { id: number }> => {
    try {
      const response = await postAPI(
        "/v5/mockup-config/background-layers",
        data,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  deleteBackgroundLayer = async (id: number): Promise<void> => {
    try {
      await deleteAPI(`/v5/mockup-config/background-layers/${id}`);
    } catch (error) {
      throw error;
    }
  };

  getPSDLayers = async (psdUrl: string): Promise<{ layer_name: string }[]> => {
    try {
      const response = await getAPI("/layers", {
        psd_url: psdUrl,
      });
      const data = response.result;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      throw error;
    }
  };

  generateMockup = async (payload: {
    psd_template_id: number;
    generated_mockup_id?: number;
    customer_id?: number;
    store_id?: number | string;
    logo_url: string;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation?: number;
    background_color?: string;
  }): Promise<{
    success: boolean;
    reference_id: string;
    outputs: { large: string; medium: string; small: string };
    message: string;
  }> => {
    const response = await postAPI(
      "/v5/mockup-config/generate-mockup",
      payload,
    );
    return response.result;
  };

  getGeneratedMockups = async (payload: {
    store_id: string;
    product_id: number;
    psd_template_id: number;
  }): Promise<GeneratedMockup[]> => {
    try {
      const response = await getAPI(
        "/v5/mockup-config/generated-mockup",
        payload,
      );
      return response.result ?? [];
    } catch (error) {
      throw error;
    }
  };

  getGeneratedMockupsByProduct = async (payload: {
    store_id: string;
    product_id: number;
    include_inactive?: boolean;
  }): Promise<GeneratedMockup[]> => {
    try {
      const response = await getAPI(
        "/v5/mockup-config/generated-mockup/all",
        payload,
      );
      return response.result ?? [];
    } catch (error) {
      throw error;
    }
  };

  deleteGeneratedMockup = async (id: number): Promise<void> => {
    try {
      const response = await deleteAPI(`/v5/mockup-config/generated-mockup/${id}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateGeneratedMockup = async (payload: {
    id: number;
    mockup_url: string;
    source: GeneratedMockupSource;
  }): Promise<GeneratedMockupActionState> => {
    try {
      const response = await postAPI("/v5/swagstore/generated-mockup/update", payload);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  approveGeneratedMockup = async (id: number): Promise<GeneratedMockupActionState> => {
    try {
      const response = await postAPI("/v5/swagstore/generated-mockup/approve", { id });
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  approveGeneratedMockupOnBehalfUser = async (payload: {
    id: number;
    reason: string;
    evidence_url: string;
  }): Promise<GeneratedMockupActionState> => {
    try {
      const response = await postAPI(
        "/v5/swagstore/generated-mockup/approve-on-behalf-user",
        payload,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  rejectGeneratedMockup = async (payload: {
    id: number;
    rejection_reason: string;
    evidence_url?: string;
  }): Promise<GeneratedMockupActionState> => {
    try {
      const response = await postAPI("/v5/swagstore/generated-mockup/reject", payload);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getGeneratedMockupComments = async (id: number): Promise<Array<{
    id: number;
    generated_mockup_id: number;
    comment: string;
    admin_id: number | null;
    user_id: number | null;
    created_at: string;
    updated_at: string;
  }>> => {
    try {
      const response = await getAPI(`/v5/swagstore/generated-mockup/${id}/comments`);
      return response.result ?? [];
    } catch (error) {
      throw error;
    }
  };

  addGeneratedMockupComment = async (payload: {
    generated_mockup_id: number;
    comment: string;
  }): Promise<{ id: number; generated_mockup_id: number; comment: string }> => {
    try {
      const response = await postAPI("/v5/swagstore/generated-mockup/comment", payload);
      return response.result;
    } catch (error) {
      throw error;
    }
  };
}
